import { useRef, useState, useEffect } from "react";

const BEFORE = "https://customer-assets.emergentagent.com/job_claude-showcase-6/artifacts/d0isck1o_ChatGPT%20Image%2026_04_2026%2C%2000_21_25.png";
const AFTER = "https://customer-assets.emergentagent.com/job_claude-showcase-6/artifacts/dqjyctet_ChatGPT%20Image%2026_04_2026%2C%2000_21_30.png";

export default function BeforeAfter() {
  const wrapRef = useRef(null);
  const [pct, setPct] = useState(50);
  const [dragging, setDragging] = useState(false);
  const [hinting, setHinting] = useState(false);
  const hasInteractedRef = useRef(false);
  const animFrameRef = useRef(null);

  const setFromClientX = (clientX) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const p = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPct(p);
  };

  const onInteract = (clientX) => {
    if (!hasInteractedRef.current) {
      hasInteractedRef.current = true;
      cancelAnimationFrame(animFrameRef.current);
      setHinting(false);
    }
    setDragging(true);
    setFromClientX(clientX);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging) return;
      if (e.cancelable) e.preventDefault();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  // Hint animation: smooth sine swing when slider enters viewport
  useEffect(() => {
    let timer;

    const runHint = () => {
      if (hasInteractedRef.current) return;
      setHinting(true);
      const startTime = performance.now();
      const duration = 1600;
      const startPct = 50;
      const amplitude = 20;

      const step = (now) => {
        if (hasInteractedRef.current) {
          setPct(startPct);
          setHinting(false);
          return;
        }
        const progress = (now - startTime) / duration;
        if (progress >= 1) {
          setPct(startPct);
          setHinting(false);
          return;
        }
        // Single smooth swing: 50 → 30 → 50
        setPct(startPct - amplitude * Math.sin(progress * Math.PI));
        animFrameRef.current = requestAnimationFrame(step);
      };

      animFrameRef.current = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasInteractedRef.current) return;
        observer.disconnect();
        timer = setTimeout(runHint, 1000);
      },
      { threshold: 0.6 }
    );

    if (wrapRef.current) observer.observe(wrapRef.current);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  return (
    <section
      id="dossier"
      data-testid="before-after-section"
      className="border-b border-rule bg-bone"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-ink/60 reveal">Dossier visual</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink reveal">
              O mesmo jardim,
              <span className="italic text-pool-deep"> reimaginado.</span>
            </h2>
            <p className="mt-5 md:mt-6 max-w-xl text-base md:text-lg text-ink/70 font-light reveal">
              Arraste para comparar. Cada simulação respeita a geometria do terreno, a orientação solar e as distâncias legais. Nada inventado, nada decorativo.
            </p>
          </div>
        </div>

        <div
          ref={wrapRef}
          data-testid="before-after-slider"
          className="relative w-full aspect-[16/10] md:aspect-[16/9] overflow-hidden select-none cursor-drag reveal-image border border-rule"
          style={{ touchAction: "none" }}
          onMouseDown={(e) => onInteract(e.clientX)}
          onTouchStart={(e) => onInteract(e.touches[0].clientX)}
        >
          {/* AFTER image — full background */}
          <img
            src={AFTER}
            alt="Depois: jardim com piscina"
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />

          {/* BEFORE image — clipped on the right via clip-path (no JS width needed) */}
          <div
            className="absolute inset-0"
            style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
          >
            <img
              src={BEFORE}
              alt="Antes: jardim sem piscina"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
          </div>

          {/* Divider line + handle */}
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
          >
            <div className="w-px h-full bg-paper shadow-[0_0_0_1px_rgba(0,0,0,0.05)]" />
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2">
              <div
                data-testid="before-after-handle"
                className={`w-12 h-12 rounded-full bg-paper flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.15)] transition-transform duration-200 ${hinting ? "scale-110" : ""}`}
              >
                <div className="flex gap-1.5">
                  <span className="w-[2px] h-3 bg-ink" />
                  <span className="w-[2px] h-3 bg-ink" />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute top-5 left-5 overline text-paper bg-ink/70 px-3 py-1.5">Antes</div>
          <div className="absolute top-5 right-5 overline text-paper bg-pool-deep/90 px-3 py-1.5">Depois</div>
        </div>

        <div className="mt-8 flex items-center justify-between reveal">
          <div className="overline text-ink/60">Ref. 014 / Moradia Cascais / 780 m² de lote</div>
          <div className="overline text-ink/60 hidden md:block">Arraste para ver</div>
        </div>
      </div>
    </section>
  );
}
